import axios from "axios";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaWhatsapp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import LanguageSwitcher from "../components/LanguageSwitcher";

const TYPES = {
  School: 1,
  University: 2,
  Other: 3,
};

const SECTORS = {
  Private: 1,
  Public: 2,
};

const InstitutesRegister = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [governorates, setGovernorates] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required(t("required")),
    governorate_id: Yup.number().required(t("required")),
    type: Yup.number().required(t("required")),
    sector: Yup.number().required(t("required")),
    phone: Yup.string().required(t("required")),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      governorate_id: "",
      type: "",
      sector: "",
      phone: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setApiError("");

      const payload = {
        ...values,
        phone: `962${values.phone.replace(/^0/, "")}`,
      };

      try {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/educational-institutions`, payload, {
          headers: {
            lang: i18n.language,
          },
        });
        setShowSuccess(true);

        setTimeout(() => {
          setShowSuccess(false);
          navigate("/auth/landing");
        }, 3000);
      } catch (error) {
        setApiError(error.response?.data?.message || "An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchGovernorates = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/governorates`, {
          headers: {
            lang: i18n.language,
          },
        });
        setGovernorates(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching governorates:", error);
        setApiError("Failed to load governorates. Please try again later.");
      }
    };

    fetchGovernorates();
  }, [i18n.language]);

  // Error message helper
  const ErrorMsg = ({ name }) =>
    formik.touched[name] && formik.errors[name] ? (
      <div className="text-sm text-red-500">{formik.errors[name]}</div>
    ) : null;

  return (
    <>
      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/+962790488840" // replace with your number in international format
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-24 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-lg transition-transform hover:scale-110 hover:shadow-xl md:bottom-5"
      >
        <FaWhatsapp size={28} />
      </a>
      <div
        className="flex min-h-screen flex-col bg-gray-100 text-sm text-[#616262]"
        dir={i18n.language === "ar" ? "rtl" : "ltr"}
      >
        {/* Top bar */}
        <div className="border-b border-gray-200 bg-white py-4">
          <div className="z-20 mx-auto flex w-full max-w-6xl items-center justify-between px-6">
            <div className="flex w-full items-center justify-between gap-4 sm:w-auto sm:justify-start">
              {/* Logo */}
              <img
                onClick={() => navigate("/auth/landing")}
                src="/logo-blue.svg"
                alt="Logo"
                className="h-10 w-auto cursor-pointer sm:h-8 ltr:pr-3 rtl:pl-3"
              />
            </div>
            {/* <LanguageSwitcher defaultTheme /> */}
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-2xl rounded-xl border border-gray-200 bg-white p-8 shadow-lg">
            {apiError && (
              <div className="mb-4 rounded-lg border border-red-500 bg-red-100 px-4 py-2 text-red-700">
                {apiError}
              </div>
            )}

            <form onSubmit={formik.handleSubmit} className="space-y-2 text-left">
              {/* Name */}
              <div>
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-[#616262]">{t("nameLabel")}</label>
                  <ErrorMsg name="name" />
                </div>
                <input
                  type="text"
                  name="name"
                  placeholder={t("namePlaceholder")}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full rounded-xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                    formik.touched.name && formik.errors.name
                      ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                      : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                  }`}
                />
              </div>

              {/* Governorate */}
              <div className="relative">
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-[#616262]">{t("cityLabel")}</label>
                  <ErrorMsg name="governorate_id" />
                </div>
                <select
                  name="governorate_id"
                  value={formik.values.governorate_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm text-[#616262] transition-all duration-300 focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                    formik.touched.governorate_id && formik.errors.governorate_id
                      ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "focus:border-purple-500 focus:ring-purple-300"
                  }`}
                >
                  <option value="" disabled className="text-[#616262]/60">
                    {t("cityPlaceholder")}
                  </option>
                  {governorates.map((gov) => (
                    <option key={gov.id} value={gov.id}>
                      {gov.name}
                    </option>
                  ))}
                </select>
                <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
              </div>

              {/* Type */}
              <div className="relative">
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-[#616262]">{t("typeLabel")}</label>
                  <ErrorMsg name="type" />
                </div>
                <select
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                    formik.touched.type && formik.errors.type
                      ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "focus:border-purple-500 focus:ring-purple-300"
                  }`}
                >
                  <option value="" disabled className="text-[#616262]/60">
                    {t("typePlaceholder")}
                  </option>
                  <option value={TYPES.School}>{t("school")}</option>
                  <option value={TYPES.University}>{t("university")}</option>
                  <option value={TYPES.Other}>{t("other")}</option>
                </select>
                <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
              </div>

              {/* Sector */}
              <div className="relative">
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-[#616262]">{t("sectorLabel")}</label>
                  <ErrorMsg name="sector" />
                </div>
                <select
                  name="sector"
                  value={formik.values.sector}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full appearance-none rounded-xl border-2 bg-white/90 px-3 py-2 text-sm text-[#616262] transition-all duration-300 focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                    formik.touched.sector && formik.errors.sector
                      ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "focus:border-purple-500 focus:ring-purple-300"
                  }`}
                >
                  <option value="" disabled>
                    {t("sectorPlaceholder")}
                  </option>
                  {Object.entries(SECTORS).map(([key, val]) => (
                    <option key={val} value={val}>
                      {t(key)}
                    </option>
                  ))}
                </select>
                <FaChevronDown className="pointer-events-none absolute top-11 -translate-y-1/2 text-[#616262] ltr:right-4 rtl:left-4" />
              </div>

              {/* Jordan Phone Number */}
              <div>
                <div className="mb-1 flex items-center gap-2">
                  <label className="block text-sm font-bold text-[#616262]">{t("phoneLabel")}</label>
                  <ErrorMsg name="phone" />
                </div>
                <div className="flex" dir="ltr">
                  <span className="flex items-center rounded-l-xl border border-r-0 bg-white px-3 text-sm text-[#616262]">
                    +962
                  </span>
                  <input
                    type="tel"
                    name="phone"
                    placeholder={t("phonePlaceholder")}
                    value={formik.values.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ""); // only digits
                      if (value.length <= 10) formik.setFieldValue("phone", value); // limit to 9 digits (excluding +962)
                    }}
                    onBlur={formik.handleBlur}
                    maxLength={10}
                    className={`w-full rounded-r-2xl border-2 px-3 py-2 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                      formik.touched.phone && formik.errors.phone
                        ? "border-2 border-red-500 bg-white/20 focus:border-red-500 focus:ring-red-500"
                        : "bg-white/20 focus:border-purple-500 focus:ring-purple-300"
                    }`}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-purple-500 py-3 text-sm text-white"
              >
                {loading ? (
                  <div className="flex flex-col items-center justify-center">
                    <div className="h-5 w-5 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"></div>
                  </div>
                ) : (
                  t("registerButton")
                )}{" "}
              </button>
            </form>
          </div>
        </div>
        <footer dir="ltr" className="mt-auto border-t border-gray-200 bg-white py-1">
          <div className="flex flex-wrap items-center justify-center gap-6">
            <a href="https://modee.gov.jo" target="_blank" rel="noopener noreferrer">
              <img
                src="/Modee2.png"
                alt="Modee"
                className="h-16 w-[75px] object-contain opacity-90 transition hover:opacity-100 sm:h-20"
              />
            </a>

            <a href="https://cpf.jo" target="_blank" rel="noopener noreferrer">
              <img
                src="/CPF2.png"
                alt="CPF"
                className="h-20 w-20 object-contain opacity-90 transition hover:opacity-100 sm:h-20"
              />
            </a>
            <a href="https://ytj.jo" target="_blank" rel="noopener noreferrer">
              <img
                src="/YTJ2.png"
                alt="YTJ"
                className="h-20 w-20 object-contain opacity-90 transition hover:opacity-100 sm:h-20"
              />
            </a>
          </div>
          <div>
            <p className="text-center text-sm text-gray-600">
              &copy; {new Date().getFullYear()} Designed and developed by HWK
            </p>
          </div>
        </footer>
      </div>
      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="animate-scale-in relative flex flex-col items-center rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-green-600">{t("RegisterSuccessfully")}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default InstitutesRegister;
